<template>
    <nav
        v-if="totalPages > 1"
        class="dn-paginator"
    >
        <slot
            name="previous"
            :emit-page-event="goToPage"
            :go-to-page="goToPage"
            :go-to-previous-page="goToPreviousPage"
            :is-disabled="currentPage === 1"
        >
            <dn-button
                element="button"
                rel="prev"
                :disabled="currentPage === 1"
                class="dn-button--clean dn-paginator__prev"
                @click="goToPage(currentPage - 1)"
            >
                <slot name="labelPrevious">&laquo;</slot>
            </dn-button>
        </slot>

        <template v-for="(map, index) in [mapLeft, mapMiddle, mapRight]">
            <template v-for="pageNumber in map">
                <slot
                    name="pageNumber"
                    :page-number="pageNumber"
                    :page-number-prefix-sr="pageNumberPrefixSr"
                    :current-page="currentPage"
                    :emit-page-event="goToPage"
                    :go-to-page="goToPage"
                    :is-active="pageNumber === currentPage"
                >
                    <dn-button
                        :key="`pager_${pageNumber}`"
                        element="button"
                        class="dn-button--clean dn-paginator__button"
                        :data-active="pageNumber === currentPage"
                        @click="goToPage(pageNumber)"
                    >
                        <span class="dn-paginator__sr-only">{{ pageNumberPrefixSr }}</span>{{ pageNumber }}
                    </dn-button>
                </slot>
            </template>

            <template v-if="(index === 0 && gapLeft) || (index === 1 && gapRight)">
                <slot name="gap">
                    <span
                        :key="`gap_${index}`"
                        class="dn-paginator__gap"
                        aria-hidden
                    >&hellip;</span>
                </slot>
            </template>
        </template>

        <slot
            name="next"
            :emit-page-event="goToPage"
            :go-to-page="goToPage"
            :go-to-next-page="goToNextPage"
            :is-disabled="currentPage >= totalPages"
        >
            <dn-button
                element="button"
                rel="next"
                :disabled="currentPage >= totalPages"
                class="dn-button--clean dn-paginator__next"
                @click="goToPage(currentPage + 1)"
            >
                <slot name="labelNext">&raquo;</slot>
            </dn-button>
        </slot>
    </nav>
</template>

<script>
import DnButton from '@digitalnatives/button';

export default {
    name: 'DnPaginator',

    components: {
        DnButton
    },

    props: {
        currentPage: {
            type: Number,
            required: true,
            default: 1
        },
        totalPages: {
            type: Number,
            required: true,
            default: 1
        },
        queryParam: {
            type: String,
            required: false,
            default: 'page'
        },
        pageNumberPrefixSr: {
            type: String,
            required: false,
            default: 'Page'
        }
    },

    emits: ['update:currentPage'],

    /** "Maps" of page sets as per https://uxframework.pearson.com/c/pagination/ */
    computed: {
        mapLeft() {
            // If there's only five pages, display all the numbers
            if (this.totalPages <= 5) {
                return Array.from(Array(this.totalPages).keys(), (_, x) => x + 1);
            }

            // The total amount of pages is greater than the maximum allowed, but we're on one of the first two
            if (this.currentPage <= 2) {
                return [1, 2, 3];
            }

            // The total amount of pages is greater than the maximum allowed, so render the first and skip the rest
            return [1];
        },

        gapLeft() {
            // If the first page in the middle mapping is the second one,
            // don't allow a left gap. As it would cause: 1 ... 2 3 4
            if (this.mapMiddle && this.mapMiddle[0] === 2) {
                return;
            }

            return this.mapMiddle;
        },

        mapMiddle() {
            // Don't map any middle pages when there's only 5 pages or less,
            // it would lead to duplicate numbers being rendered.
            if (this.totalPages <= 5) {
                return null;
            }

            // The total amount of pages is greater than the maximum to display,
            // and the current page is not one of the first or final two.
            if (this.currentPage > 2 && this.currentPage <= (this.totalPages - 2)) {
                return [this.currentPage - 1, this.currentPage, this.currentPage + 1];
            }

            return null;
        },

        gapRight() {
            return this.mapRight;
        },

        mapRight() {
            // Only render this set of the total amount of pages is greater than 5
            if (this.totalPages > 5) {
                // The total amount of pages is greater than the maximum to display, and we're on one of the first three,
                // or the middle set is active, so render just the final number
                if (this.currentPage <= 3 || this.mapMiddle) {
                    return [this.totalPages];
                }

                // We're on one of the final three pages, so render the final three numbers
                return [this.totalPages - 2, this.totalPages - 1, this.totalPages];
            }

            return null;
        }
    },

    methods: {
        // Alias for backwards-compatibility
        emitPageEvent(pageNumber) {
            this.goToPage(pageNumber, false);
        },

        goToPreviousPage() {
            this.goToPage(this.currentPage - 1);
        },

        goToNextPage() {
            this.goToPage(this.currentPage + 1);
        },

        goToPage(pageNumber, doRoute = true) {
            if (!this.$router) {
                doRoute = false;
            }

            if (pageNumber < 1) {
                this.$emit('update:currentPage', 1);
                if (doRoute && this.queryParam) {
                    this.$router.push(this.getPageUrl(1));
                }
                return;
            }

            if (pageNumber > this.totalPages) {
                this.$emit('update:currentPage', this.totalPages);
                if (doRoute && this.queryParam) {
                    this.$router.push(this.getPageUrl(this.totalPages));
                }
                return;
            }

            this.$emit('update:currentPage', pageNumber);

            if (doRoute && this.queryParam) {
                this.$router.push(this.getPageUrl(pageNumber));
            }
        },

        getPageUrl(pageNumber) {
            if (!this.queryParam) {
                return '';
            }

            if (pageNumber < 1) {
                return '';
            }

            if (pageNumber > this.totalPages) {
                return '';
            }

            return { query: { ...this.$route.query, [this.queryParam]: pageNumber } };
        }
    }
};
</script>

<style lang="less" src="./paginator.less" />
